import { useState } from 'react';
import type { Route } from '../lib/routes';
import type { AppSession, MeView } from '../lib/types';
import type { AuthConfig } from '../lib/home';
import { ApiError, api } from '../lib/api';
import { goTo } from '../lib/routes';
import { displayName } from '../lib/session';
import { AppMark, Icon } from '../components/Mark';
import { Card } from '../components/Bits';
import { PracticeSetup } from './PracticeSetup';

/**
 * A signed-in person who belongs to no practice yet. Two doors: join the demo practice as a patient
 * where the deployment allows it, or found a practice of their own.
 */
export function JoinPage({ session, me, config, route, onSignOut, onChanged, orgReturn, clearOrgReturn }: { session: AppSession; me: MeView; config: AuthConfig | null; route: Route; onSignOut: () => void; onChanged: () => Promise<void>; orgReturn: { code: string; state: string } | null; clearOrgReturn: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (route.page === 'practice-setup') {
    return <PracticeSetup session={session} config={config} onDone={onChanged} orgReturn={orgReturn} clearOrgReturn={clearOrgReturn} onBack={() => goTo('#/')} />;
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-inner">
          <span className="brand">
            <AppMark size={22} />
            <span>OncoCoach</span>
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="quiet" onClick={onSignOut} style={{ color: 'rgba(255,255,255,0.75)' }}>
            Sign out
          </button>
        </div>
      </header>

      <main className="shell shell-narrow">
        <section className="banner">
          <div className="banner-main">
            <span className="eyebrow">Welcome</span>
            <h1>Hello, {displayName(session)}.</h1>
            <p className="sub">You are signed in, and not enrolled with a practice yet.</p>
          </div>
        </section>

        <div className="stack">
          {me.demoPractice?.canJoin ? (
            <Card>
              <div className="card-head">
                <h2>Try OncoCoach as a patient</h2>
                <span className="badge">Test data</span>
              </div>
              <p className="hint">
                Join {me.demoPractice.name}, the demo practice, under Dr. Maulik Shah, with a fortnight of sample history so the app has something to show. Play coins only.
              </p>
              {error ? <p className="form-error">{error}</p> : null}
              <button
                className="primary"
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await api.demoJoin(session.token);
                    await onChanged();
                    goTo('#/welcome');
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? 'Enrolling…' : `Join ${me.demoPractice.name}`}
              </button>
            </Card>
          ) : (
            <Card>
              <h2>Waiting to be prescribed</h2>
              <p className="hint">OncoCoach is prescribed by your oncology team. Once your practice enrolls you, this page becomes your dashboard.</p>
            </Card>
          )}

          <Card>
            <div className="card-head">
              <h2>Run a practice?</h2>
              <span className="bubble bubble-green bubble-sm">
                <Icon name="shield" size={16} />
              </span>
            </div>
            <p className="hint">Found your practice as an organization at your Home, then add your doctors and enroll your patients here.</p>
            <a className="secondary button" href="#/practice/setup">
              Set up a practice
            </a>
          </Card>
        </div>
      </main>
    </div>
  );
}
